import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile, browserLocalPersistence, setPersistence, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { startDeviceSession, endDeviceSession, clearSession, setSignOutNotice } from '../utils/deviceSession';

const clearLocalAuth = () => {
  localStorage.removeItem('db_role'); localStorage.removeItem('db_role_uid'); localStorage.removeItem('db_user_name');
};

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  // Owner set a temporary password → this user must pick their own first.
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;
    setPersistence(auth, browserLocalPersistence).then(() => {
      if (cancelled) return;
      unsub = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);
          // Fast path: this device already knows this user's role — render now and
          // confirm it from Firestore below, instead of holding every page (the
          // kitchen board included) on a blank spinner for the round trip. Keyed
          // to the uid so a different account on the same device never inherits it.
          let cachedRole = null;
          try { if (localStorage.getItem('db_role_uid') === firebaseUser.uid) cachedRole = localStorage.getItem('db_role'); } catch {}
          if (cachedRole && cachedRole !== 'undefined' && cachedRole !== 'null') { setRole(cachedRole); setLoading(false); }
          let userRole = 'customer';
          let firestoreName = null;
          try {
            const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000));
            const snap = await Promise.race([getDoc(doc(db, 'users', firebaseUser.uid)), timeout]);
            userRole = snap.exists() ? snap.data().role : (localStorage.getItem('db_role') || 'customer');
            firestoreName = snap.exists() ? snap.data().name : null;
            setMustChangePassword(snap.exists() && snap.data().mustChangePassword === true);
          } catch {
            userRole = localStorage.getItem('db_role') || 'customer';
          }
          localStorage.setItem('db_role', userRole);
          localStorage.setItem('db_role_uid', firebaseUser.uid);
          // Store display name for analytics tracker (Firestore name takes priority over Auth displayName)
          const userName = firestoreName || firebaseUser.displayName || firebaseUser.email || '';
          localStorage.setItem('db_user_name', userName);
          setRole(userRole);
        } else {
          // Signed out by any path (logout, owner sign-out, revoked token):
          // the next sign-in on this device is a new session.
          clearSession();
          setUser(null);
          setRole(null);
          setMustChangePassword(false);
        }
        setLoading(false);
      });
    });
    // Unsubscribe on unmount (StrictMode mounts twice in development, which
    // otherwise doubled every sign-in's Firestore read).
    return () => { cancelled = true; unsub(); };
  }, []);

  // Register this device so the owner can see it on /sessions, and sign out
  // here the moment the owner signs this device (or this account) out.
  const uid = user?.uid;
  useEffect(() => {
    if (!uid || !auth.currentUser) return undefined;
    return startDeviceSession(auth.currentUser, async () => {
      setSignOutNotice('You were signed out of this device by the owner. Sign in again to continue.');
      await endDeviceSession(auth.currentUser); // clears the owner's "waiting" row
      clearLocalAuth();
      await signOut(auth).catch(() => {});
      // Full reload, not an in-app redirect: they sign back in on the latest build.
      window.location.replace('/login');
    });
  }, [uid]);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const signup = async (email, password, name, selectedRole = 'customer') => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    try {
      await setDoc(doc(db, 'users', cred.user.uid), {
        name,
        email,
        role: selectedRole,
        createdAt: new Date().toISOString(),
      });
    } catch (firestoreErr) {
      console.warn('Could not save user role to Firestore:', firestoreErr);
    }
    setRole(selectedRole);
    localStorage.setItem('db_role', selectedRole);
    localStorage.setItem('db_role_uid', cred.user.uid);
    return cred;
  };

  const logout = async () => {
    await endDeviceSession(auth.currentUser);
    clearLocalAuth();
    return signOut(auth);
  };

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const passwordChanged = () => setMustChangePassword(false);

  const value = { user, role, loading, login, signup, logout, resetPassword, mustChangePassword, passwordChanged };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
